import { useCallback, useEffect, useState } from "react";

import { setCurrentFloorID, setCurrentPoiID } from "@/features/cartography/cartographySlice";
import {
  selectCurrentFloorID,
  selectCurrentPoiID,
} from "@/features/cartography/cartographySlice.selectors";
import { selectConfigItem } from "@/features/config/configSlice";
import { setFollowUser } from "../map/mapSlice";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { useRefListener } from "@/app/hooks/useRefListener";
import { handleSetCamera } from "@/features/map/actions";
import {
  EXPIRATION_CHECK_INTERVAL_MS,
  REFRESH_SHARED_LOCATION_ERROR_RETRY_MS,
  REFRESH_SHARED_LOCATION_MS,
  ReceivedLocationStatus,
  resetReceivedSharedLocation,
  selectReceivedSessionId,
  selectReceivedSharedLocation,
  setReceivedLocationStatus,
  setReceivedSharedLocation,
} from "@/features/share_location";
import { useSitumAPIContext } from "@/features/situm_api/SitumAPIProvider";
import { isExpired } from "@/features/utils/date";
import { type Camera, SHARED_LOCATION_ID } from "@/libs/models";
import { updateUrl } from "@/libs/url";

export const useReceivedLocationShare = () => {
  const dispatch = useAppDispatch();
  const { getSharedLocationById } = useSitumAPIContext();
  const sharedLocationReceived = useAppSelector(selectReceivedSharedLocation);
  const receivedSessionId = useAppSelector(selectReceivedSessionId);
  const currentFloorID = useAppSelector(selectCurrentFloorID);
  const currentPoiID = useAppSelector(selectCurrentPoiID);
  const [refreshInterval, setRefreshInterval] = useState(
    REFRESH_SHARED_LOCATION_MS,
  );
  const isShareLocationActive = useAppSelector(
    selectConfigItem("feature_flags.share_location"),
  );

  const stopWatching = useCallback(() => {
    dispatch(resetReceivedSharedLocation());
    updateUrl({ shared_session_id: null });
  }, [dispatch]);

  const onFetchSharedLocationRef = useRefListener(() => {
    if (!receivedSessionId || !isShareLocationActive) return;

    getSharedLocationById({ locationSessionId: receivedSessionId })
      .then((res) => {
        // !res means that there is shared location but not position
        if (!res) {
          // OOB, offline, etc
          setRefreshInterval(REFRESH_SHARED_LOCATION_ERROR_RETRY_MS);
          dispatch(
            setReceivedLocationStatus(ReceivedLocationStatus.UNAVAILABLE),
          );
          // set current poi id if there was no previous shared location value
          if (sharedLocationReceived) return;
          dispatch(setCurrentPoiID(SHARED_LOCATION_ID));
          return;
        }
        // OUTDOOR
        if (!res.floorId) {
          dispatch(
            setReceivedLocationStatus(ReceivedLocationStatus.UNAVAILABLE),
          );
          // Just update expiration timestamp
          dispatch(
            setReceivedSharedLocation({
              ...sharedLocationReceived,
              expirationTimestamp: res.expirationTimestamp,
            }),
          );
          // set current poi id if there was no previous shared location value
          if (sharedLocationReceived) return;
          dispatch(setCurrentPoiID(SHARED_LOCATION_ID));
          return;
        }
        // TODO: what if shared location does not match same building user is at
        // what to do? prevent from it, clear location, move the view to that building?

        dispatch(setReceivedLocationStatus(ReceivedLocationStatus.ONGOING));
        setRefreshInterval(REFRESH_SHARED_LOCATION_MS);
        dispatch(setReceivedSharedLocation(res));
        const isFirstTime = !sharedLocationReceived;
        if (!isFirstTime) return;
        dispatch(setCurrentPoiID(SHARED_LOCATION_ID));
        dispatch(setCurrentFloorID(res.floorId));
        dispatch(setFollowUser(false));
        handleSetCamera({
          center: {
            lat: res.coordinates.lat,
            lng: res.coordinates.lng,
          },
        } as Camera);
      })
      .catch((err) => {
        stopWatching();
        console.error("Failed to fetch shared location:", err);
      });
  }, [
    receivedSessionId,
    getSharedLocationById,
    dispatch,
    stopWatching,
    sharedLocationReceived,
    currentFloorID,
    currentPoiID,
  ]);

  // Periodic fetch of shared location
  useEffect(() => {
    if (!receivedSessionId || !isShareLocationActive) return;

    const interval = setInterval(
      () => onFetchSharedLocationRef.current(),
      refreshInterval,
    );

    return () => clearInterval(interval);
  }, [receivedSessionId, refreshInterval, isShareLocationActive]);

  // Timer for received location
  useEffect(() => {
    if (!sharedLocationReceived) return;

    const interval = setInterval(() => {
      if (isExpired(sharedLocationReceived.expirationTimestamp)) {
        stopWatching();
      }
    }, EXPIRATION_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [sharedLocationReceived?.expirationTimestamp, stopWatching]);
};
